import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './BlogPostPage.scss';

const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [blogPost, setBlogPost] = useState(null);
  const [recentPosts, setRecentPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Dummy blog data
  const blogData = [
    {
      id: 1,
      slug: 'solar-power-transforms-rural-kenya',
      title: 'Solar Power Transforms Rural Kenya: A Community Success Story',
      excerpt: 'How a small village in Kenya achieved 100% renewable energy access through innovative solar solutions and community collaboration.',
      content: `
        <p>In the heart of rural Kenya, a quiet revolution is taking place. The village of Kitui, once dependent on expensive and unreliable diesel generators, has transformed into a beacon of sustainable energy innovation. This is the story of how solar power changed everything.</p>

        <h3>The Challenge</h3>
        <p>For decades, the 2,000 residents of Kitui village faced daily struggles with energy access. Children studied by candlelight, businesses closed early due to lack of lighting, and healthcare facilities couldn't operate essential equipment. The cost of diesel fuel consumed nearly 40% of household incomes, trapping families in a cycle of energy poverty.</p>

        <h3>The Solution</h3>
        <p>In 2022, the Vasudha Africa Energy Database partnered with local organizations to implement a comprehensive solar microgrid system. The project included:</p>
        <ul>
          <li>Installation of 500kW solar panels across the village</li>
          <li>Battery storage systems for 24/7 power availability</li>
          <li>Smart metering for efficient energy distribution</li>
          <li>Training programs for local technicians</li>
        </ul>

        <h3>Impact and Results</h3>
        <p>The transformation has been remarkable. Within six months of implementation:</p>
        <ul>
          <li>100% of households now have reliable electricity</li>
          <li>Local businesses have seen 60% increase in revenue</li>
          <li>School attendance has improved by 45%</li>
          <li>Healthcare services operate 24/7</li>
          <li>Carbon emissions reduced by 80%</li>
        </ul>

        <h3>Community Ownership</h3>
        <p>What makes this project truly special is the community's active involvement. Local residents formed a cooperative to manage the system, with trained technicians maintaining the infrastructure. This approach ensures long-term sustainability and creates local employment opportunities.</p>

        <p>"This solar project has given us hope for the future," says Mary Wanjiku, a local shop owner. "My children can now study at night, and my business stays open longer. We're not just surviving anymore – we're thriving."</p>

        <h3>Lessons Learned</h3>
        <p>The Kitui project demonstrates that successful energy transitions require more than just technology. Community engagement, local capacity building, and sustainable business models are equally important. The project has become a model for similar initiatives across East Africa.</p>

        <p>As we continue to track energy projects across Africa, stories like Kitui remind us of the human impact behind every data point. Each solar panel installed, each household connected, represents a family's journey from energy poverty to energy prosperity.</p>
      `,
      bannerImage: 'https://images.unsplash.com/photo-1509391366360-2e959784a276?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      publishDate: '2024-01-15',
      author: 'Dr. Sarah Mwangi',
      category: 'Renewable Energy',
      readTime: '8 min read',
      tags: ['Solar Power', 'Rural Development', 'Kenya', 'Community Impact']
    },
    {
      id: 2,
      slug: 'wind-energy-morocco-desert',
      title: 'Harnessing the Desert Winds: Morocco\'s Renewable Energy Revolution',
      excerpt: 'Morocco\'s ambitious wind energy projects in the Sahara Desert are setting new standards for renewable energy development in Africa.',
      content: `
        <p>Morocco's vast Sahara Desert, once seen as an inhospitable wasteland, is now home to one of the world's most ambitious renewable energy projects. The Noor Ouarzazate Solar Complex and the Tarfaya Wind Farm represent a bold vision for Africa's energy future.</p>

        <h3>The Vision</h3>
        <p>Morocco's renewable energy strategy aims to generate 52% of its electricity from renewable sources by 2030. This ambitious target is being achieved through massive investments in solar and wind energy infrastructure across the country's desert regions.</p>

        <h3>Technical Innovation</h3>
        <p>The projects showcase cutting-edge technology:</p>
        <ul>
          <li>Concentrated Solar Power (CSP) with thermal storage</li>
          <li>Advanced wind turbines designed for desert conditions</li>
          <li>Smart grid integration for optimal energy distribution</li>
          <li>Desalination plants powered by renewable energy</li>
        </ul>

        <h3>Economic Impact</h3>
        <p>The renewable energy sector has created over 50,000 jobs in Morocco, with significant opportunities for local communities. The projects have also attracted international investment and positioned Morocco as a leader in clean energy technology.</p>

        <h3>Environmental Benefits</h3>
        <p>These projects are expected to reduce Morocco's carbon emissions by 3.7 million tons annually, contributing significantly to global climate goals while providing clean, reliable energy to millions of people.</p>
      `,
      bannerImage: 'https://images.unsplash.com/photo-1559827260-dc66d52bef19?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      publishDate: '2024-01-10',
      author: 'Ahmed Benali',
      category: 'Wind Energy',
      readTime: '6 min read',
      tags: ['Wind Energy', 'Morocco', 'Desert', 'Innovation']
    },
    {
      id: 3,
      slug: 'hydroelectric-ethiopia-nile',
      title: 'The Grand Ethiopian Renaissance Dam: Powering a Nation\'s Future',
      excerpt: 'Ethiopia\'s massive hydroelectric project on the Blue Nile represents a new era of energy independence and regional cooperation.',
      content: `
        <p>The Grand Ethiopian Renaissance Dam (GERD) stands as a testament to Ethiopia's determination to harness its natural resources for national development. This 6,450 MW hydroelectric project is the largest of its kind in Africa and represents a significant milestone in the continent's energy transformation.</p>

        <h3>Project Overview</h3>
        <p>Located on the Blue Nile River, the GERD project includes:</p>
        <ul>
          <li>Main dam with 74 billion cubic meters storage capacity</li>
          <li>Two powerhouses with 16 Francis turbines</li>
          <li>Transmission lines connecting to the national grid</li>
          <li>Environmental monitoring and mitigation systems</li>
        </ul>

        <h3>National Impact</h3>
        <p>The dam is expected to double Ethiopia's electricity generation capacity, providing power to over 65 million people who currently lack access to electricity. This represents a fundamental transformation in the country's energy landscape.</p>

        <h3>Regional Cooperation</h3>
        <p>While the project has generated regional discussions about water sharing, it also presents opportunities for increased energy trade and cooperation among Nile Basin countries. The dam's power generation could benefit the entire region through interconnected grids.</p>

        <h3>Sustainable Development</h3>
        <p>The GERD project aligns with Ethiopia's commitment to sustainable development, providing clean energy while supporting economic growth and social development across the country.</p>
      `,
      bannerImage: 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      publishDate: '2024-01-05',
      author: 'Dr. Tewodros Gebre',
      category: 'Hydroelectric',
      readTime: '7 min read',
      tags: ['Hydroelectric', 'Ethiopia', 'Nile', 'Infrastructure']
    },
    {
      id: 4,
      slug: 'geothermal-kenya-rift-valley',
      title: 'Geothermal Energy in Kenya\'s Rift Valley: Tapping Earth\'s Power',
      excerpt: 'Kenya\'s Rift Valley is home to some of the world\'s most advanced geothermal energy projects, providing clean and reliable power to millions.',
      content: `
        <p>Kenya's Great Rift Valley, a geological wonder formed by tectonic activity, is also home to one of the world's most successful geothermal energy programs. The Olkaria Geothermal Complex represents decades of innovation and investment in clean energy technology.</p>

        <h3>Geological Advantage</h3>
        <p>Kenya's location along the East African Rift System provides unique access to geothermal resources. The country has an estimated potential of 10,000 MW of geothermal energy, making it one of the world's leading geothermal power producers.</p>

        <h3>Technology and Innovation</h3>
        <p>The Olkaria complex showcases advanced geothermal technology:</p>
        <ul>
          <li>Binary cycle power plants for low-temperature resources</li>
          <li>Enhanced geothermal systems (EGS) technology</li>
          <li>Advanced drilling techniques for deep reservoirs</li>
          <li>Integrated steam and power generation systems</li>
        </ul>

        <h3>Economic Benefits</h3>
        <p>Geothermal energy has become Kenya's most reliable and cost-effective power source, providing over 40% of the country's electricity generation. The industry has created thousands of jobs and attracted significant international investment.</p>

        <h3>Environmental Impact</h3>
        <p>Geothermal energy produces virtually no greenhouse gas emissions and has a minimal environmental footprint compared to fossil fuel alternatives. The projects also support local biodiversity conservation efforts.</p>

        <h3>Future Potential</h3>
        <p>With ongoing exploration and development, Kenya aims to increase its geothermal capacity to 5,000 MW by 2030, further solidifying its position as a global leader in geothermal energy development.</p>
      `,
      bannerImage: 'https://images.unsplash.com/photo-1466611653911-95081537e5b7?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      publishDate: '2024-01-01',
      author: 'Prof. James Mwangi',
      category: 'Geothermal',
      readTime: '6 min read',
      tags: ['Geothermal', 'Kenya', 'Rift Valley', 'Clean Energy']
    },
    {
      id: 5,
      slug: 'solar-mini-grids-tanzania',
      title: 'Solar Mini-Grids Transform Rural Tanzania: Powering Progress',
      excerpt: 'How decentralized solar mini-grids are bringing reliable electricity to remote villages in Tanzania, enabling economic growth and improving quality of life.',
      content: `
        <p>In the remote villages of Tanzania's rural regions, a quiet energy revolution is taking place. Solar mini-grids are transforming communities that have never had access to reliable electricity, opening doors to new opportunities and improved living standards.</p>

        <h3>The Challenge of Rural Electrification</h3>
        <p>For decades, rural Tanzania has faced significant challenges in accessing electricity. The national grid reaches only about 40% of the population, leaving millions of people in rural areas without reliable power. This energy poverty has limited economic opportunities, educational access, and healthcare services.</p>

        <h3>Solar Mini-Grid Solution</h3>
        <p>Solar mini-grids offer an innovative solution to rural electrification challenges. These decentralized systems combine solar panels, battery storage, and smart distribution networks to provide reliable electricity to small communities. The approach is particularly effective in remote areas where extending the national grid would be prohibitively expensive.</p>

        <h3>Community Impact</h3>
        <p>The implementation of solar mini-grids has brought remarkable changes to rural communities:</p>
        <ul>
          <li>Local businesses can operate longer hours with reliable lighting</li>
          <li>Students can study after dark, improving educational outcomes</li>
          <li>Healthcare facilities can operate essential medical equipment</li>
          <li>Mobile phone charging enables better communication</li>
          <li>Agricultural processing becomes more efficient</li>
        </ul>

        <h3>Economic Benefits</h3>
        <p>The economic impact of reliable electricity access has been significant. Local entrepreneurs have started new businesses, existing enterprises have expanded their operations, and communities have seen increased economic activity. The mini-grids also create local employment opportunities in maintenance and operation.</p>

        <h3>Sustainable Development</h3>
        <p>Solar mini-grids contribute to sustainable development by providing clean, renewable energy that reduces dependence on fossil fuels. The systems are designed for long-term operation with minimal environmental impact, supporting Tanzania's commitment to sustainable energy development.</p>
      `,
      bannerImage: 'https://images.unsplash.com/photo-1497435334941-8c899ee9e8e9?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      publishDate: '2023-12-28',
      author: 'Dr. Fatuma Hassan',
      category: 'Solar Energy',
      readTime: '5 min read',
      tags: ['Solar Mini-Grids', 'Tanzania', 'Rural Development', 'Decentralized Energy']
    },
    {
      id: 6,
      slug: 'wind-farm-south-africa',
      title: 'South Africa\'s Wind Energy Revolution: Harnessing Coastal Winds',
      excerpt: 'South Africa\'s ambitious wind energy program along its coastline is setting new standards for renewable energy development and creating thousands of jobs.',
      content: `
        <p>South Africa's extensive coastline, blessed with consistent and powerful winds, has become the foundation for one of Africa's most ambitious wind energy programs. The country's wind farms are not only generating clean electricity but also driving economic development and job creation.</p>

        <h3>Wind Resource Potential</h3>
        <p>South Africa's coastal regions, particularly along the Western and Eastern Cape provinces, offer exceptional wind resources. The consistent trade winds and favorable topography create ideal conditions for wind energy generation, with capacity factors often exceeding 40%.</p>

        <h3>Major Wind Projects</h3>
        <p>The country has developed several large-scale wind farms that showcase advanced technology and engineering:</p>
        <ul>
          <li>Jeffreys Bay Wind Farm - 138 MW capacity</li>
          <li>Gouda Wind Farm - 138 MW capacity</li>
          <li>Noblesfontein Wind Farm - 73.8 MW capacity</li>
          <li>Cookhouse Wind Farm - 138.6 MW capacity</li>
        </ul>

        <h3>Economic Impact</h3>
        <p>The wind energy sector has created over 15,000 direct and indirect jobs in South Africa. Local manufacturing of wind turbine components has developed, creating a domestic supply chain that supports the industry's growth. The sector has also attracted significant foreign investment, contributing to the country's economic development.</p>

        <h3>Environmental Benefits</h3>
        <p>Wind energy has helped South Africa reduce its carbon emissions while diversifying its energy mix. The clean electricity generated by wind farms displaces coal-fired power generation, contributing to the country's climate change mitigation efforts and improving air quality.</p>

        <h3>Community Development</h3>
        <p>Wind farm developers have implemented comprehensive community development programs that include education, healthcare, and economic development initiatives. These programs ensure that local communities benefit directly from wind energy projects in their areas.</p>
      `,
      bannerImage: 'https://images.unsplash.com/photo-1547036967-23d11aacaee0?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      publishDate: '2023-12-20',
      author: 'Prof. Thabo Mbeki',
      category: 'Wind Energy',
      readTime: '6 min read',
      tags: ['Wind Energy', 'South Africa', 'Coastal Winds', 'Job Creation']
    },
    {
      id: 7,
      slug: 'solar-irrigation-nigeria',
      title: 'Solar-Powered Irrigation Transforms Nigerian Agriculture',
      excerpt: 'Innovative solar irrigation systems are revolutionizing farming in Nigeria, increasing crop yields and reducing water usage while providing clean energy.',
      content: `
        <p>In Nigeria's agricultural heartland, solar-powered irrigation systems are transforming traditional farming practices. These innovative solutions are helping farmers increase crop yields, reduce water consumption, and access reliable energy for agricultural operations.</p>

        <h3>The Agricultural Challenge</h3>
        <p>Nigerian farmers have long faced challenges with unreliable electricity and expensive diesel fuel for irrigation pumps. These constraints have limited agricultural productivity and prevented many farmers from expanding their operations or growing high-value crops that require consistent irrigation.</p>

        <h3>Solar Irrigation Technology</h3>
        <p>Solar-powered irrigation systems combine photovoltaic panels with efficient water pumps to provide reliable, cost-effective irrigation. The systems include:</p>
        <ul>
          <li>High-efficiency solar panels for energy generation</li>
          <li>Smart water pumps with variable speed control</li>
          <li>Water storage tanks for consistent supply</li>
          <li>Drip irrigation systems for efficient water delivery</li>
          <li>Remote monitoring and control systems</li>
        </ul>

        <h3>Farmer Success Stories</h3>
        <p>Farmers using solar irrigation systems have reported significant improvements in their operations. Crop yields have increased by 30-50%, water usage has been reduced by 40%, and operational costs have decreased substantially. Many farmers have been able to grow crops year-round, increasing their income and food security.</p>

        <h3>Economic Impact</h3>
        <p>The adoption of solar irrigation has created new economic opportunities in rural Nigeria. Farmers can now grow high-value crops, access new markets, and increase their income. The technology has also created local jobs in installation, maintenance, and support services.</p>

        <h3>Sustainable Agriculture</h3>
        <p>Solar irrigation promotes sustainable agricultural practices by reducing dependence on fossil fuels and optimizing water usage. The systems help preserve groundwater resources while enabling more efficient and productive farming methods.</p>
      `,
      bannerImage: 'https://images.unsplash.com/photo-1500382017468-9049fed747ef?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      publishDate: '2023-12-15',
      author: 'Dr. Aisha Ibrahim',
      category: 'Solar Energy',
      readTime: '7 min read',
      tags: ['Solar Irrigation', 'Nigeria', 'Agriculture', 'Water Conservation']
    }
  ];

  useEffect(() => {
    // Simulate loading
    setTimeout(() => {
      const post = blogData.find(blog => blog.slug === slug);
      const recent = blogData.filter(blog => blog.slug !== slug).slice(0, 3);
      
      setBlogPost(post);
      setRecentPosts(recent);
      setLoading(false);
    }, 500);
  }, [slug]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleRecentPostClick = (postSlug) => {
    navigate(`/blog/${postSlug}`);
    // Scroll to top will be handled by ScrollToTop component
  };

  if (loading) {
    return (
      <div className="blog-post-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading blog post...</p>
        </div>
      </div>
    );
  }

  if (!blogPost) {
    return (
      <div className="blog-post-page">
        <div className="error-container">
          <h2>Blog Post Not Found</h2>
          <p>The blog post you're looking for doesn't exist.</p>
          <button onClick={() => navigate('/')} className="back-btn">
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="blog-post-page">
      {/* Blog Post Header */}
      <div className="blog-header">
        <div className="blog-banner">
          <img src={blogPost.bannerImage} alt={blogPost.title} />
          <div className="banner-overlay">
            <div className="container">
              <div className="blog-meta">
                <span className="category">{blogPost.category}</span>
                <span className="read-time">{blogPost.readTime}</span>
              </div>
              <h1 className="blog-title">{blogPost.title}</h1>
              <div className="blog-info">
                <span className="author">By {blogPost.author}</span>
                <span className="publish-date">{formatDate(blogPost.publishDate)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Blog Content */}
      <div className="blog-content">
        <div className="container">
          <div className="content-wrapper">
            <div className="main-content">
              <div 
                className="blog-text"
                dangerouslySetInnerHTML={{ __html: blogPost.content }}
              />
              
              <div className="blog-tags">
                <h4>Tags:</h4>
                <div className="tags-list">
                  {blogPost.tags.map((tag, index) => (
                    <span key={index} className="tag">{tag}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Blog Posts */}
      <div className="recent-posts-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Recent Stories from the Field</h2>
            <p className="section-subtitle">Explore more inspiring energy transformation stories across Africa</p>
          </div>
          
          <div className="recent-posts-grid">
            {recentPosts.map((post) => (
              <div 
                key={post.id} 
                className="recent-post-card"
                onClick={() => handleRecentPostClick(post.slug)}
              >
                <div className="post-image">
                  <img src={post.bannerImage} alt={post.title} />
                  <div className="post-category">{post.category}</div>
                </div>
                <div className="post-content">
                  <h3 className="post-title">{post.title}</h3>
                  <p className="post-excerpt">{post.excerpt}</p>
                  <div className="post-meta">
                    <span className="post-author">{post.author}</span>
                    <span className="post-date">{formatDate(post.publishDate)}</span>
                    <span className="post-read-time">{post.readTime}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogPostPage;
